import './index.scss';

import Modal from "@components/Modal"
import { useEffect, useMemo, useState } from "react";
import XLSX from "xlsx";
import closeIcon from 'url:@icon/close.svg';
import ErrorView from '@components/ErrorView';
import EventsController, { Event, EventAttendee, Waiver } from '@data/controller/EventsController';
import SuccessView from '@components/SuccessView';
import LoadingIndicator from '@components/LoadingIndicator';
import { useAuth } from '@auth/AuthProvider';

type ProposedUpdate = {
  newlyCompleted: EventAttendee[]
  failedToFindMatch: string[]
}

export default function ManualWaiverUploadModal(props: {
  event: Event,
  attendees: EventAttendee[],
  isOpen: boolean,
  close: () => void,
  onAttendeesUpdated: (attendees: EventAttendee[]) => void
}) {
  const auth = useAuth();

  const [pendingSelectedWaiver, setPendingSelectedWaiver] = useState<Waiver | null>(null);
  const [selectedWaiver, setSelectedWaiver] = useState<Waiver | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [sheetJSON, setSheetJSON] = useState<unknown[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(null);
  const [proposedUpdate, setProposedUpdate] = useState<ProposedUpdate | null>(null);

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const createProposedUpdates = () => {
    if (selectedColumnIndex == null || !sheetJSON || !selectedWaiver) {
      return;
    }

    let newlyCompleted: EventAttendee[] = [];
    let failedToFindMatch: string[] = [];

    // Take off header
    for (const row of sheetJSON.slice(1)) {
      const name = (row as unknown[])[selectedColumnIndex];
      if (!name || typeof name !== 'string') continue;

      let replace = name.replace(/[^a-zA-Z0-9 ,]/g, ' ').trim();
      let split = replace.split(', ').filter(s => s.length > 0);

      for (const item of split) {
        const matchingAttendee = props.attendees.find(attendee => {
          const attendeeName = attendee.name.toLowerCase();
          return attendeeName === item.toLowerCase()
        });

        if (matchingAttendee) {
          // only add if attendee hasn't completed waiver.
          if (!matchingAttendee.completedWaivers.find(w => w.id === selectedWaiver.id)) {
            newlyCompleted.push(matchingAttendee);
          }
        } else {
          failedToFindMatch.push(item);
        }
      }
    }

    setProposedUpdate({
      newlyCompleted,
      failedToFindMatch
    });
  };

  const submitAttendeeUpdate = async (markAsComplete: EventAttendee[]) => {
    if (!selectedWaiver) return;

    setSaving(true);
    setError(null);

    try {
      const response = await EventsController.submitAttendeeWaiverCompletions(
        props.event.churchID,
        props.event.id,
        selectedWaiver.id,
        markAsComplete,
        auth
      );

      if (response.error || !response.body) {
        setError(response.error || "Failed to save updates. Please try again later.");
        return;
      }

      props.onAttendeesUpdated(response.body.attendees);
      setSuccess(true);
    }
    catch (err) {
      setError("Failed to save updates. Please try again later.");
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!props.isOpen) return;

    setError(null);
    setFile(null);
    setSheetJSON(null);
    setSelectedColumnIndex(null);
    setProposedUpdate(null);
    setSelectedWaiver(null);
    setPendingSelectedWaiver(null);
    setSaving(false);
    setSuccess(false);
  }, [props.isOpen]);

  useEffect(() => {
    if (!file) return;

    const parseFile = async () => {
      try {
        const ab = await file.arrayBuffer();
        const workbook = XLSX.read(ab, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];

        if (firstSheetName) {
          const sheet = workbook.Sheets[firstSheetName];
          const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          setSheetJSON(data);
        } else {
          setError("Failed to parse sheets from file. Please contact support with a copy of your spreadsheet.");
        }
      } catch (err) {
        setError("Failed to parse sheets from file. Please contact support with a copy of your spreadsheet.");
      }
    };

    parseFile();
  }, [file]);

  const closeButton = <a className="close"
    href="#"
    onClick={(e) => {
      e.preventDefault();
      props.close()
    }}>
    <img className="icon" src={closeIcon} alt="Close icon" />
  </a>;

  return <Modal isOpen={props.isOpen} id="manual-waiver-upload" footerButtons={[]}>
    {saving && <LoadingIndicator fullScreen={false} />}

    {!error && !saving && !success && <div className="header">
      {closeButton}
      <div />
      <h3>Manual Waiver Upload</h3>
      <div />
    </div>}

    {error && <div style={{ marginTop: 24 }}>
      {closeButton}
      <ErrorView error={error} showButton={false} />
    </div>}

    {!error && !selectedWaiver && <div className='selectWaiver'>
      <p>Please select the waiver which you would like to update attendee status for.</p>
      <select value={pendingSelectedWaiver ? pendingSelectedWaiver.id : 'null'} onChange={(e) => {
        const waiver = props.event.waivers.find(w => w.id === e.target.value);
        setPendingSelectedWaiver(waiver || null);
      }}>
        <option value={'null'} disabled={true}>-- Select Waiver --</option>

        {props.event.waivers.map((waiver) => (
          <option key={waiver.id} value={waiver.id}>{waiver.name}</option>
        ))}
      </select>

      <button disabled={!pendingSelectedWaiver} onClick={(e) => {
        e.preventDefault();
        setSelectedWaiver(pendingSelectedWaiver);
      }}>
        Next
      </button>
    </div>}

    {!error && selectedWaiver && !sheetJSON && <>
      <p>Please select a spreadsheet (CSV or Excel files supported)</p>
      <input type="file" name="spreadsheet" accept=".xlsx,.xls,.csv" onChange={(e) => {
        e.preventDefault();
        setFile(e.target.files?.[0] || null);
      }} />
    </>}

    {!error && sheetJSON && !proposedUpdate && <div className='columnSelect'>
      <p>Please select which column contains the names of attendees</p>
      <select value={selectedColumnIndex === null ? 'null' : selectedColumnIndex} onChange={(e) => {
        if (e.target.value === 'null') {
          setSelectedColumnIndex(null);
          return;
        }

        setSelectedColumnIndex(parseInt(e.target.value));
      }}>
        <option value={'null'} disabled={true}>-- Select Column --</option>

        {sheetJSON[0] && (sheetJSON[0] as unknown[]).map((col, idx) => (
          <option key={idx} value={idx}>{col || `Column ${idx + 1}`}</option>
        ))}
      </select>
      <button disabled={selectedColumnIndex === null} onClick={(e) => {
        e.preventDefault();
        createProposedUpdates();
      }}>Next</button>
    </div>}

    {!error && proposedUpdate && selectedWaiver && !saving && !success && <ProposedUpdateEditor proposedUpdate={proposedUpdate}
      attendees={props.attendees}
      waiver={selectedWaiver}
      onConfirm={(markAsComplete) => {
        submitAttendeeUpdate(markAsComplete);
      }} />}

    {success && <div style={{ marginTop: 24 }}>
      {closeButton}
      <SuccessView title='Waiver Submissions Updated' description='Successfully updated the waiver submissions for this event.' />
    </div>}
  </Modal>
}

function ProposedUpdateEditor(props: {
  waiver: Waiver,
  proposedUpdate: ProposedUpdate,
  attendees: EventAttendee[],
  onConfirm: (markAsComplete: EventAttendee[]) => void
}) {
  const { proposedUpdate } = props;
  const [participantOverride, setParticipantOverride] = useState<Record<string, string>>({});

  const availableAttendees = useMemo(() => {
    const newlyCompletedIds = new Set(proposedUpdate.newlyCompleted.map(a => a.id));

    return props.attendees
      .filter(attendee => !newlyCompletedIds.has(attendee.id)) // not recently completed
      .filter(attendee => attendee.completedWaivers.find(w => w.id == props.waiver.id) == null) // not already completed
      .filter(attendee => !Object.values(participantOverride).includes(attendee.id)); // not already selected as override
  }, [props.attendees, participantOverride]);

  return <div className='proposedUpdates'>
    {proposedUpdate.newlyCompleted.length > 0 && <>
      <h4>Proposed Status Updates</h4>
      <p>These attendees will be marked as having recently completed the selected waiver.</p>

      <ul className='attendeeList'>
        {proposedUpdate.newlyCompleted.sort((a, b) => {
          if (a.name === b.name) return 0;
          return a.name < b.name ? -1 : 1;
        }).map((attendee, idx) => (
          <li key={idx} className='attendee'>
            {attendee.name}
          </li>
        ))}
      </ul>
    </>}

    {proposedUpdate.failedToFindMatch.length > 0 && <>
      <h4>Unmatched Names</h4>
      <p>The following names could not be matched to any attendees who have not already completed the selected waiver. You may manually select an attendee per unmatched name, or leave blank to skip.</p>

      <ul className='attendeeList'>
        {proposedUpdate.failedToFindMatch.sort((a, b) => {
          if (a === b) return 0;
          return a < b ? -1 : 1;
        }).map((name, idx) => {
          const fuzzyMatch = props.attendees.find(attendee => {
            const attendeeName = attendee.name.toLowerCase();

            const attendeeNameSplit = attendeeName.split(" ");
            const nameSplit = name.toLowerCase().split(" ");

            for (const part of attendeeNameSplit) {
              for (const namePart of nameSplit) {
                if (part.toLowerCase() === namePart.toLowerCase()) {
                  return true;
                }
              }
            }

            return false;
          });

          return <li key={idx} className='attendee'>
            {name}
            <select value={participantOverride[name] || fuzzyMatch?.id || 'null'}
              onChange={(e) => {
                const selected = e.target.value;
                const newOverrides = { ...participantOverride };

                if (selected === 'null') {
                  delete newOverrides[name];
                } else {
                  newOverrides[name] = selected;
                }

                setParticipantOverride(newOverrides);
                console.log(newOverrides);
              }}>

              <option value={'null'}>-- Select Attendee --</option>
              {props.attendees.sort((a, b) => {
                if (a.name === b.name) return 0;
                return a.name < b.name ? -1 : 1;
              }).map((attendee) => (
                <option key={attendee.id} value={attendee.id} disabled={!availableAttendees.includes(attendee) && attendee.id !== participantOverride[name]}>{attendee.name}</option>
              ))}
            </select>
          </li>
        })}
      </ul>
    </>
    }

    {
      proposedUpdate.failedToFindMatch.length === 0 && proposedUpdate.newlyCompleted.length === 0 &&
      <p>No updates were found in the uploaded spreadsheet. Either the spreadsheet was impossible to read, or all attendees within have already been marked as having completed the selected waiver.</p>
    }

    <button onClick={(e) => {
      e.preventDefault();

      const finalNewlyCompleted = [...proposedUpdate.newlyCompleted];

      for (const [_, attendeeId] of Object.entries(participantOverride)) {
        const attendee = props.attendees.find(a => a.id === attendeeId);
        if (attendee) {
          finalNewlyCompleted.push(attendee);
        }
      }

      props.onConfirm(finalNewlyCompleted);
    }}>Confirm Update</button>
  </div >
}